import React, { useState, useEffect } from 'react';
import './UserReportViewer.css';

const UserReportViewer = ({ csvUrl }) => {
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('データを読み込み中...');
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    const fetchCsvData = async () => {
      try {
        setLoading(true);
        setLoadingProgress(0);
        setLoadingMessage('ファイルをダウンロード中...');
        
        const response = await fetch(csvUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        setLoadingProgress(20);
        setLoadingMessage('CSVデータを解析中...');
        
        const csvText = await response.text();
        
        setLoadingProgress(40);
        setLoadingMessage('データを処理中...');
        
        // CSVの改行文字を正規化
        const cleanedCsvText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        
        // 行ごとに分割
        const lines = cleanedCsvText.split('\n').filter(line => line.trim() !== '');
        
        setLoadingProgress(60);
        setLoadingMessage('データ構造を構築中...');
        
        if (lines.length > 0) {
          // CSVパーサー関数: ダブルクォート付きフィールドを正しく処理
          const parseCSVLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              
              if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                  // エスケープされたダブルクォート
                  current += '"';
                  i++; // 次のダブルクォートをスキップ
                } else {
                  // クォートの開始/終了
                  inQuotes = !inQuotes;
                }
              } else if (char === ',' && !inQuotes) {
                // フィールドの区切り
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            
            // 最後のフィールドを追加
            result.push(current.trim());
            return result;
          };
          
          const headers = parseCSVLine(lines[0]);
          
          // 大きなファイルの場合はバッチ処理でメモリ使用量を抑制
          const batchSize = 1000;
          const data = [];
          
          for (let i = 1; i < lines.length; i += batchSize) {
            const batch = lines.slice(i, i + batchSize);
            const batchData = batch.map((line, lineIndex) => {
              try {
                const values = parseCSVLine(line);
                const row = {};
                headers.forEach((header, index) => {
                  let fieldValue = values[index] || '';
                  // すべてのフィールドで改行文字を半角スペースに変換
                  fieldValue = fieldValue.replace(/\r\n/g, ' ').replace(/\r/g, ' ').replace(/\n/g, ' ');
                  row[header] = fieldValue;
                });
                return row;
              } catch (error) {
                console.error(`行 ${i + lineIndex + 1} の解析エラー:`, error, line);
                return null;
              }
            }).filter(row => row !== null);
            
            data.push(...batchData);
            
            // 進捗更新
            const progress = 60 + Math.floor((i / lines.length) * 30);
            setLoadingProgress(progress);
            setLoadingMessage(`データ処理中... ${Math.floor((i / lines.length) * 100)}%`);
          }
          
          setLoadingProgress(90);
          setLoadingMessage('フィルター情報を抽出中...');
          
          setHeaders(headers);
          setCsvData(data);
          setFilteredData(data);
          
          // 利用可能な年・月・日・ユーザーを抽出
          extractAvailableFilters(data);
          
          setLoadingProgress(100);
          setLoadingMessage('完了！');
          
          // 少し待ってからローディング状態を解除
          setTimeout(() => {
            setLoading(false);
          }, 500);
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (csvUrl) {
      fetchCsvData();
    }
  }, [csvUrl]);

  const extractAvailableFilters = (data) => {
    const years = new Set();
    const months = new Set();
    const days = new Set();
    const users = new Set();

    data.forEach(row => {
      // 日付フィールドが空の場合、現在の日付を使用するか、フィルターを無効にする
      if (row['日付'] && row['日付'].trim() !== '') {
        const dateParts = row['日付'].split('-');
        if (dateParts.length === 3) {
          years.add(dateParts[0]);
          months.add(dateParts[1]);
          days.add(dateParts[2]);
        }
      }
      if (row['氏名'] && row['氏名'].trim() !== '') {
        users.add(row['氏名']);
      }
    });

    setAvailableYears(Array.from(years).sort());
    setAvailableMonths(Array.from(months).sort());
    setAvailableDays(Array.from(days).sort());
    setAvailableUsers(Array.from(users).sort());
  };

  useEffect(() => {
    applyFilters();
  }, [csvData, selectedYear, selectedMonth, selectedDay, selectedUser]);

  const applyFilters = () => {
    let filtered = [...csvData];

    // 年フィルター（日付フィールドが存在し、空でない場合のみ）
    if (selectedYear) {
      filtered = filtered.filter(row => {
        if (!row['日付'] || row['日付'].trim() === '') return false;
        const dateParts = row['日付'].split('-');
        return dateParts[0] === selectedYear;
      });
    }

    // 月フィルター（日付フィールドが存在し、空でない場合のみ）
    if (selectedMonth) {
      filtered = filtered.filter(row => {
        if (!row['日付'] || row['日付'].trim() === '') return false;
        const dateParts = row['日付'].split('-');
        return dateParts[1] === selectedMonth;
      });
    }

    // 日フィルター（日付フィールドが存在し、空でない場合のみ）
    if (selectedDay) {
      filtered = filtered.filter(row => {
        if (!row['日付'] || row['日付'].trim() === '') return false;
        const dateParts = row['日付'].split('-');
        return dateParts[2] === selectedDay;
      });
    }

    // ユーザーフィルター
    if (selectedUser) {
      filtered = filtered.filter(row => row['氏名'] === selectedUser);
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setSelectedUser('');
    setExpandedRows(new Set());
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleRowExpansion = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return timeStr;
  };

  const formatComment = (comment) => {
    if (!comment) return '-';
    return comment;
  };

  // ページネーション計算
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="user-report-viewer">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{loadingMessage}</p>
          <div className="loading-progress-bar">
            <div className="loading-progress" style={{ width: `${loadingProgress}%` }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-report-viewer">
        <div className="error-container">
          <h3>エラーが発生しました</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-report-viewer">
      <div className="viewer-header">
        <h2>利用者日報ビューアー</h2>
        <p>利用者の日報データを検索・表示できます</p>
      </div>

      {/* フィルターセクション */}
      <div className="filter-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>年:</label>
            <select 
              value={selectedYear} 
              onChange={(e) => setSelectedYear(e.target.value)}
              className="filter-select"
            >
              <option value="">すべて</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}年</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>月:</label>
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="filter-select"
            >
              <option value="">すべて</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>{month}月</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>日:</label>
            <select 
              value={selectedDay} 
              onChange={(e) => setSelectedDay(e.target.value)}
              className="filter-select"
            >
              <option value="">すべて</option>
              {availableDays.map(day => (
                <option key={day} value={day}>{day}日</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>利用者:</label>
            <select 
              value={selectedUser} 
              onChange={(e) => setSelectedUser(e.target.value)}
              className="filter-select"
            >
              <option value="">すべて</option>
              {availableUsers.map(user => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-row">
          <button onClick={clearFilters} className="clear-filters-button">
            フィルタークリア
          </button>
        </div>
      </div>

      {/* 結果サマリー */}
      <div className="results-summary">
        <p>検索結果: {filteredData.length}件</p>
        {filteredData.length > 0 && (
          <p>表示中: {startIndex + 1}-{Math.min(endIndex, filteredData.length)}件</p>
        )}
      </div>

      {/* データテーブル */}
      <div className="table-container">
        {currentData.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>日付</th>
                <th>氏名</th>
                <th>作業時間</th>
                <th>申告時間</th>
                <th>日報タイトル</th>
                <th>詳細</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((row, index) => (
                <React.Fragment key={index}>
                  <tr className="data-row">
                    <td>{row['日付'] || '-'}</td>
                    <td>{row['氏名'] || '-'}</td>
                    <td>
                      <span className="time-range">
                        {formatTime(row['作業時間'])}
                      </span>
                    </td>
                    <td>{formatTime(row['申告時間'])}</td>
                    <td className="report-title">
                      {row['日報タイトル'] || '-'}
                    </td>
                    <td>
                      <button 
                        onClick={() => toggleRowExpansion(startIndex + index)}
                        className="expand-button"
                      >
                        {expandedRows.has(startIndex + index) ? '▼' : '▶'}
                      </button>
                    </td>
                  </tr>
                  {expandedRows.has(startIndex + index) && (
                    <tr className="detail-row">
                      <td colSpan="6">
                        <div className="detail-content">
                          <h4>日報コメント</h4>
                          <div className="comment-content">
                            {formatComment(row['日報コメント'])}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-data">
            <p>該当するデータが見つかりませんでした。</p>
            <p>フィルター条件を変更してお試しください。</p>
          </div>
        )}
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            前へ
          </button>
          
          <span className="page-info">
            {currentPage} / {totalPages}
          </span>
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            次へ
          </button>
        </div>
      )}
    </div>
  );
};

export default UserReportViewer; 